/**
 *
 * ColFormItemWrapper
 *
 */

import React, { useEffect } from "react";
import { Col, Form, Tooltip } from "antd";
import PropTypes from "prop-types";
import { FiHelpCircle } from "react-icons/fi";
import { colorPrimary } from "../../../../root";
import { isFunction } from "webpack-merge/dist/utils";
import { AiFillEye, AiOutlineEye } from "react-icons/ai";
import { EditFilled, EditOutlined } from "@ant-design/icons";
import { generalFormatAntd } from "../../../../utils/dayjs";
import { validateGoogleAddress } from "../form.utils";
import { t } from "i18next";

// import PropTypes from 'prop-types';
// import styled from 'styled-components';

function uppercase(s) {
  return s.toUpperCase();
}

function capitalize(s) {
  return s && s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function removeDecimal(s) {
  if (s === null) {
    return 0;
  }

  return parseFloat(s)
    .toFixed(3)
    .slice(0, -1);
}

function ColFormItemWrapper({ item, index, children, refForm }) {
  const [canRenderWatch, setCanRenderWatch] = React.useState(
    item.watch === undefined,
  );
  const [canRenderWatchDynamic, setCanRenderWatchDynamic] = React.useState(
    item.watchDynamicObject === undefined,
  );

  const [itemWatched, setItemWatched] = React.useState(null);

  const watchedValue = item.watch ? Form.useWatch(item.watch) : null;
  const watchedDynamicValue = item.watchDynamicObject
    ? Form.useWatch(item.watchDynamicObject)
    : null;

  const filterChildrenList = child => {
    if (item.filterContent === undefined) {
      return true;
    } else {
      return item.filterContent(child, itemWatched);
    }
  };
  const previousItemWatched = React.useRef();

  useEffect(() => {
    if (item.watchDynamicObject) {
      const dataDynamic = refForm.current[`_${item.watchDynamicObject}`];

      // first one is dataDynamic
      // i want the previous one
      if (dataDynamic !== undefined) {
        let itemWatchedRef = dataDynamic.find(
          e => e._id === watchedDynamicValue,
        );

        // if (item.name === "applicant") {
        // }

        if (itemWatchedRef !== undefined) {
          setItemWatched(itemWatchedRef);
          setCanRenderWatchDynamic(true);

          // If the item should be dynamically set to empty
          if (item.watchDynamicSetEmpty) {
            if (canRenderWatch && canRenderWatchDynamic) {
              const initValue = refForm.current.getFieldValue(item.name);

              // if (item.name === "applicant") {
              // }

              // init case
              if (initValue === undefined || initValue === "") {
                if (item.watchDynamicSetter) {
                  refForm.current.setFieldsValue({
                    [item.name]: item.watchDynamicSetter(itemWatched),
                  });
                }
              }

              const hasPreviousId =
                previousItemWatched.current?._id !== undefined;

              const hasCurrentId = itemWatchedRef._id !== undefined;

              // Update one !
              if (
                itemWatched._id !== itemWatchedRef._id &&
                item.watchDynamicSetter
              ) {
                // IDs are different, which means the watcher value has changed. Update the field value.
                refForm.current.setFieldsValue({
                  [item.name]: item.watchDynamicSetter(itemWatchedRef),
                });
              } else if (itemWatched._id !== itemWatchedRef._id) {
                // reset if no dynamic settter
                refForm.current.setFieldsValue({
                  [item.name]: null,
                });
              }
            }
          } else {
            const initValue = refForm.current.getFieldValue(item.name);

            // If not set to watch dynamic changes, simply reset the field value to undefined.
            if (item.name) {
              refForm.current.setFieldsValue({
                [item.name]: undefined,
              });
            }
          }

          if (itemWatched !== null && itemWatchedRef._id !== itemWatched?._id) {
            previousItemWatched.current = itemWatchedRef;
          }
        } else {
          console.log(
            "How can you watch a value does not exist ? Deleted item in the DB",
            item.name,
          );
        }
      } else {
        setCanRenderWatchDynamic(false);
      }
    }
    // Watch the name
    if (item.watch) {
      // Watch dynamic values
      // watch static values
      if (item.watchValues) {
        const isWatchedValueArray = Array.isArray(watchedValue);

        // Log the debug information once

        // Function to set the rendering based on the inclusion check
        const updateCanRenderWatch = value => {
          const isIncluded = item.watchValues.includes(value);
          setCanRenderWatch(isIncluded);
          return isIncluded;
        };

        // Check if watchedValue is an array and handle accordingly
        if (isWatchedValueArray) {
          // Use `.some` to find if any value is included and set render state accordingly
          const isAnyIncluded = watchedValue.some(wv =>
            updateCanRenderWatch(wv),
          );
          if (!isAnyIncluded) setCanRenderWatch(false); // Explicitly set false if no values are included
        } else {
          // Single value check
          updateCanRenderWatch(watchedValue);
        }
      }

      if (item.watchValuesCallback) {
        const itemValue = refForm.current.getFieldValue(item.name);
        item.watchValuesCallback(itemValue, watchedValue, refForm);
      }
    }
  }, [
    watchedValue,
    watchedDynamicValue,
    canRenderWatch,
    canRenderWatchDynamic,
  ]);

  // TODO deal with initialValue and Value and rehyrate

  const { name, offset, displayLabel, rules = [], value, col } = item;

  const {
    label,
    quote,
    quoteIcon = "info",
    quoteStyle,
    componentLabel,
    required,
    addonAfter,
    addonBefore,
    suffixIcon,
    watchValues,
    watchRenderCondition,
    endPoint,
    description,
    itemDisabled,
    selectContent,
    ...rest
  } = item;

  const extraProps = JSON.parse(JSON.stringify(rest));

  if (item.type === "submit" || item.type === "checkbox-bool") {
    extraProps.label = null;
  }

  if (
    item.type === "input" &&
    item.capitalize !== undefined &&
    item.capitalize !== false
  ) {
    extraProps.normalize = value => capitalize(value);
  }
  if (item.type === "input" && item.uppercase === true) {
    extraProps.normalize = value => uppercase(value);
  }

  if (item.normalize !== undefined && isFunction(item.normalize)) {
    extraProps.normalize = value => item.normalize(value);
  }

  if (item.type !== "dynamic-select") {
    extraProps.initialValue = value;
  } else {
    if (value !== undefined && value !== null) {
      extraProps.initialValue = value;
    }
  }

  if (item.type === "select") {
    if (value === undefined || value?.length === 0) {
      // extraProps.initialValue = undefined;
    } else {
      extraProps.initialValue = value;
    }
  }

  if (item.type === "input-currency") {
    extraProps.validateTrigger = "onBlur";
    extraProps.trigger = "onBlur";
  }
  if (item.type === "checkbox-bool") {
    extraProps.valuePropName = "checked";
  }

  // if (item.type === 'input-currency') {
  //   extraProps.trigger = 'onValueChange';
  // }

  const renderQuote = () => {
    if (quote === undefined) {
      return null;
    } else {
      return (
        <Tooltip
          title={quote}
          color={"#343434"}
          showArrow={true}
          overlayStyle={
            quoteStyle === "large"
              ? {
                  maxWidth: "500px",
                }
              : {}
          }
        >
          {quoteIcon === "info" && (
            <FiHelpCircle
              color={colorPrimary}
              size={20}
              style={{ paddingLeft: 1, paddingRight: 1, marginLeft: 5 }}
            />
          )}
          {quoteIcon === "edit" && (
            <EditFilled
              color={colorPrimary}
              size={20}
              style={{
                paddingLeft: 6,
                paddingRight: 5,
                color: colorPrimary,
              }}
            />
          )}
          {quoteIcon === "eye" && (
            <AiFillEye
              color={colorPrimary}
              size={20}
              style={{ paddingLeft: 1, paddingRight: 1, marginLeft: 5 }}
            />
          )}
        </Tooltip>
      );
    }
  };

  const renderLabel = () => {
    if (label === undefined && componentLabel === undefined) {
      return null;
    }

    if (label === null) {
      return null;
    }

    if (componentLabel === undefined) {
      return (
        <>
          {label} {renderQuote()}
        </>
      );
    }

    if (React.isValidElement(componentLabel)) {
      return (
        <>
          {componentLabel} {renderQuote()}
        </>
      );
    }
  };

  let extraClassName = "";

  if (item.variant === "important") {
    extraClassName = " important-form";
  }

  const renderChildren = () => {
    if (typeof children === "function") {
      return children({ filterChildrenList });
    }
    if (React.isValidElement(children)) {
      // Generate a unique key for the child element
      const uniqueKey = `${name}-${index}-child`;
      return React.cloneElement(children, { key: uniqueKey });
    }
    console.error("Invalid children passed to ColFormItemWrapper");
    return null;
  };

  // const canRenderWatchDynamic = () => {
  //   if (item.watchDynamicObject) {
  //     return itemWatched !== null;
  //   } else {
  //     return true;
  //   }
  // };

  const controlledRender = () => {
    if (children === null) {
      return false;
    }

    if (item.watchRenderCondition) {
      return canRenderWatch && canRenderWatchDynamic;
    } else {
      return true;
    }
  };

  const controllerRequiredRender = () => {
    if (item.watchRequiredCondition) {
      return canRenderWatch;
    } else {
      return item.required;
    }
  };

  const defaultRules = [
    {
      required: controllerRequiredRender() || false,
      message: t("common.mandatoryField"),
    },
  ];

  return controlledRender() ? (
    <Col
      style={{
        display: item.type === "input-hidden" ? "none" : "block",
        opacity: item.ready === false || item.disabled === true ? 0.8 : 1,
        textAlign: item.align ? item.align : "left",
      }}
      key={`formItemWrapper-${name}-${index}-col`}
      span={col}
      xs={item.xs}
      sm={item.sm}
      md={item.md}
      lg={item.lg}
      xl={item.xl}
    >
      <Form.Item
        required={controllerRequiredRender()}
        key={`${name}-${index}-form-item`}
        name={name}
        label={renderLabel()}
        className={name + "-wrapper " + extraClassName}
        {...extraProps}
        rules={rules.length === 0 ? defaultRules : rules}
      >
        {renderChildren()}
      </Form.Item>
    </Col>
  ) : null;
}

ColFormItemWrapper.propTypes = {
  item: PropTypes.shape({
    col: PropTypes.number,
    name: PropTypes.string,
    label: PropTypes.string,
    offset: PropTypes.number,
    displayLabel: PropTypes.bool,
    required: PropTypes.bool,
    value: PropTypes.any,
  }),
  rules: PropTypes.array,
  index: PropTypes.number,
  // children: PropTypes.node,
};

ColFormItemWrapper.defaultProps = {
  item: {
    rules: [],
  },
};

export default ColFormItemWrapper;
